import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useGlobalSearch } from '../../hooks/useGlobalSearch.ts';
import SearchResultRow from './SearchResultRow.tsx';
import SearchPreview from './SearchPreview.tsx';
import { navigateToResult } from './searchNav.ts';
import {
  sortTypes, typeColor, TYPE_LABELS,
  getRecentSearches, pushRecentSearch,
  getRecentlyViewed,
  type SearchResultData, type RecentlyViewedEntry,
} from './searchTypes.ts';
import { typeIcon } from './searchIcons.tsx';
import './GlobalSearch.css';

interface Section { type: string; shown: SearchResultData[]; total: number }

export default function GlobalSearch({ autoFocus, placeholder = 'Search items, vendors, orders...' }: {
  autoFocus?: boolean;
  placeholder?: string;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeType, setActiveType] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 0 });

  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { results, counts, total, loading } = useGlobalSearch(query, activeType);
  const tokens = useMemo(() => query.trim().split(/\s+/).filter(Boolean), [query]);
  const hasQuery = query.trim().length >= 2;

  // Reset the keyboard-active row whenever the result set changes underneath it.
  useEffect(() => { setActiveIndex(0); }, [results]);

  // Clear the type filter back to "All" whenever that type no longer has any results left for
  // the current query -- otherwise a stale filter chip could keep the list permanently empty.
  useEffect(() => {
    if (activeType && hasQuery && !loading && (counts[activeType] ?? 0) === 0) setActiveType(null);
  }, [counts, activeType, hasQuery, loading]);

  const sections: Section[] = useMemo(() => {
    const byType = new Map<string, SearchResultData[]>();
    for (const r of results) {
      if (!byType.has(r.type)) byType.set(r.type, []);
      byType.get(r.type)!.push(r);
    }
    return sortTypes([...byType.keys()]).map((type) => ({
      type, shown: byType.get(type)!, total: counts[type] ?? byType.get(type)!.length,
    }));
  }, [results, counts]);

  const chipTypes = useMemo(() => sortTypes(Object.keys(counts).filter((t) => (counts[t] ?? 0) > 0)), [counts]);

  // ── Positioning (portal, so the dropdown is never clipped by the topbar) ──────────────────
  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    function reposition() {
      const rect = anchorRef.current!.getBoundingClientRect();
      const width = Math.min(980, window.innerWidth - 32);
      const left = Math.min(Math.max(16, rect.left), window.innerWidth - width - 16);
      setPanelPos({ top: rect.bottom + 8, left, width });
    }
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  // ── Click outside closes ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── Ctrl/Cmd+K focuses from anywhere (desktop instance only -- the mobile instance only
  // exists while already expanded/focused, so it doesn't need the global shortcut). ───────────
  useEffect(() => {
    if (autoFocus) return;
    function handler(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [autoFocus]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  function select(r: SearchResultData | RecentlyViewedEntry) {
    navigateToResult(navigate, r);
    setQuery('');
    setOpen(false);
  }

  // Shared by Esc (both the input's own onKeyDown and the document-level listener below) and the
  // clear (X) button -- closes the panel, clears the query, and blurs the input so nothing
  // reappears (the empty-query "Recent searches" panel included).
  const closeAndClear = useCallback(() => {
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  }, []);

  // Esc must close on the FIRST press, and must work even when focus has moved off the input
  // (onto a chip, a result row, or a preview action button) -- a document-level listener catches
  // that case; the input's own onKeyDown below still handles it too while focus is still there
  // (harmless to run closeAndClear twice for the same keypress).
  useEffect(() => {
    if (!open) return;
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') closeAndClear();
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, closeAndClear]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = results[activeIndex];
      if (r) select(r);
      else if (query.trim()) pushRecentSearch(query);
    } else if (e.key === 'Tab' && open) {
      e.preventDefault();
      const chipValues: (string | null)[] = [null, ...chipTypes];
      const curIdx = chipValues.indexOf(activeType);
      const dir = e.shiftKey ? -1 : 1;
      const next = chipValues[(curIdx + dir + chipValues.length) % chipValues.length];
      setActiveType(next);
    } else if (e.key === 'Escape') {
      closeAndClear();
    }
  }

  const flatIndexOf = (r: SearchResultData) => results.indexOf(r);

  return (
    <div className="gs-anchor" ref={anchorRef}>
      <div className="gs-input-wrap">
        <Search size={15} color="var(--text-muted)" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="gs-listbox"
          placeholder={placeholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {loading && <span className="gs-spinner" aria-hidden="true" />}
        {!autoFocus && !query && <span className="gs-kbd-hint">⌘K</span>}
        {query && (
          <button type="button" className="gs-clear-btn" onClick={closeAndClear} aria-label="Clear search">
            <X size={13} />
          </button>
        )}
      </div>

      {open && ReactDOM.createPortal(
        <>
          <div className="gs-scrim" />
          <div
            ref={panelRef}
            className="gs-panel"
            style={{ top: panelPos.top, left: panelPos.left, width: panelPos.width }}
          >
            {hasQuery && chipTypes.length > 0 && (
              <div className="gs-chips">
                <button type="button" className={`gs-chip${activeType === null ? ' active' : ''}`} onClick={() => setActiveType(null)}>
                  All
                </button>
                {chipTypes.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`gs-chip${activeType === t ? ' active' : ''}`}
                    onClick={() => setActiveType((cur) => (cur === t ? null : t))}
                  >
                    <span className="gs-chip-dot" style={{ background: typeColor(t) }} />
                    {TYPE_LABELS[t] ?? t} · {counts[t] ?? 0}
                  </button>
                ))}
              </div>
            )}

            <div className="gs-body">
              <div className="gs-list" id="gs-listbox" role="listbox">
                {!hasQuery ? (
                  <EmptyState onPickRecent={(q) => setQuery(q)} onPickViewed={select} />
                ) : results.length === 0 && !loading ? (
                  <div className="gs-no-results">No matches for &quot;{query}&quot;. Try a part number, brand, or room.</div>
                ) : (
                  sections.map((sec) => (
                    <div key={sec.type}>
                      <div className="gs-section-label">
                        <span className="gs-chip-dot" style={{ background: typeColor(sec.type) }} />
                        {TYPE_LABELS[sec.type] ?? sec.type}
                        {sec.total > sec.shown.length && (
                          <button type="button" className="gs-view-all" onClick={() => setActiveType(sec.type)}>
                            View all {sec.total} →
                          </button>
                        )}
                      </div>
                      {sec.shown.map((r) => {
                        const idx = flatIndexOf(r);
                        return (
                          <SearchResultRow
                            key={`${r.type}-${r.id}`}
                            result={r}
                            tokens={tokens}
                            active={idx === activeIndex}
                            onClick={() => select(r)}
                            onMouseEnter={() => setActiveIndex(idx)}
                          />
                        );
                      })}
                    </div>
                  ))
                )}
              </div>

              {hasQuery && results.length > 0 && results[activeIndex] && (
                <div className="gs-preview">
                  <SearchPreview result={results[activeIndex]} onNavigated={() => { setQuery(''); setOpen(false); }} />
                </div>
              )}
            </div>

            <div className="gs-footer">
              <div className="gs-footer-hints">
                <span><kbd>↑↓</kbd> navigate</span>
                <span><kbd>↵</kbd> open</span>
                <span><kbd>Tab</kbd> next filter</span>
                <span><kbd>Esc</kbd> close</span>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

function EmptyState({ onPickRecent, onPickViewed }: {
  onPickRecent: (q: string) => void;
  onPickViewed: (r: RecentlyViewedEntry) => void;
}) {
  const recentSearches = getRecentSearches();
  const recentlyViewed = getRecentlyViewed();
  if (recentSearches.length === 0 && recentlyViewed.length === 0) {
    return <div className="gs-no-results">Start typing to search the whole library.</div>;
  }
  return (
    <div className="gs-empty-state">
      {recentSearches.length > 0 && (
        <>
          <div className="gs-empty-group-label">Recent searches</div>
          <div className="gs-recent-chips">
            {recentSearches.map((q) => (
              <button key={q} type="button" className="gs-recent-chip" onClick={() => onPickRecent(q)}>{q}</button>
            ))}
          </div>
        </>
      )}
      {recentlyViewed.length > 0 && (
        <>
          <div className="gs-empty-group-label">Recently viewed</div>
          {recentlyViewed.map((r) => {
            const Icon = typeIcon(r.type);
            return (
              <div key={`${r.type}-${r.id}`} className="gs-recent-viewed-row" onClick={() => onPickViewed(r)}>
                <div className="gs-thumb gs-thumb-dark gs-thumb-cover" style={{ width: 32, height: 32 }}>
                  {r.thumbnailUrl ? <img src={r.thumbnailUrl} alt="" loading="lazy" /> : <div className="gs-empty-icon-frame"><Icon size={14} /></div>}
                </div>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)' }}>{r.title}</span>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
